/**
 * GiftCardDetailPage — `/admin/gift-cards/:id` (BON-223).
 *
 * Summary (masked number, status, balance, customer) plus the ledger that
 * explains it. There is no resolve/void flow here yet.
 */
import { useLocation, useParams } from 'react-router-dom';

import { useViewport } from '../../hooks/useViewport';
import { DashboardsPage } from '../dashboards/DashboardsPage';
import { ListPageHeader } from '../shared/list-page/ListPageHeader';

import { GiftCardDetailBody } from './GiftCardDetailBody';
import { GiftCardEmailFailedWarning } from './GiftCardEmailFailedWarning';
import { GiftCardsErrorBanner } from './GiftCardsErrorBanner';
import { GiftCardsLoadingState } from './GiftCardsLoadingState';
import { cardNumberLabel } from './giftCardsModel';
import { useGiftCardDetailState } from './useGiftCardDetailState';

import '../dashboards/dashboards.css';

const LIST_PATH = '/admin/gift-cards';

export default function GiftCardDetailPage() {
  const { id } = useParams<{ id: string }>();
  const s = useGiftCardDetailState(id ?? '');
  const { isDesktop } = useViewport();
  const emailFailedFor = (useLocation().state as { emailFailedFor?: string } | null)
    ?.emailFailedFor;

  if (s.loading) {
    return (
      <DashboardsPage
        header={
          <ListPageHeader
            crumb="Customers › Gift cards"
            title="Gift card"
            backTo={LIST_PATH}
            backLabel="Back to gift cards"
          />
        }
      >
        <GiftCardsLoadingState label="Loading this gift card" />
      </DashboardsPage>
    );
  }

  if (!s.detail) {
    return (
      <DashboardsPage
        header={
          <ListPageHeader
            crumb="Customers › Gift cards"
            title="Gift card"
            backTo={LIST_PATH}
            backLabel="Back to gift cards"
          />
        }
      >
        <GiftCardsErrorBanner message={s.error ?? 'That gift card could not be loaded.'} />
      </DashboardsPage>
    );
  }

  const title = cardNumberLabel(s.detail);

  return (
    <DashboardsPage
      header={
        <ListPageHeader
          crumb={`Customers › Gift cards › ${title}`}
          title={`Gift card ${title}`}
          backTo={LIST_PATH}
          backLabel="Back to gift cards"
        />
      }
      stack={false}
    >
      {emailFailedFor && <GiftCardEmailFailedWarning email={emailFailedFor} />}
      <GiftCardDetailBody card={s.detail} isDesktop={isDesktop} />
    </DashboardsPage>
  );
}
